import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cases, assessments } from '../services/api';
import { EnrollmentManager, TeamManager } from '../components/enrollment';
import { CaseContentManager } from '../components/case';
import GrupsExamen from '../components/case/GrupsExamen';
import MarkdownViewer from '../components/common/MarkdownViewer';
import AvisCopia from '../components/common/AvisCopia';
import { nomExecutor } from '../utils/usuari';
import { useProteccioCopia } from '../utils/proteccioCopia';

const DIFFICULTY_LABELS = {
  beginner: 'Principiant',
  intermediate: 'Intermedi',
  advanced: 'Avançat'
};

const STATUS_LABELS = {
  in_progress: 'En progrés',
  submitted: 'Enviat',
  graded: 'Qualificat',
  paused: 'Pausat',
  abandoned: 'Abandonat',
  time_expired: 'Temps esgotat'
};

function CaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [assessmentList, setAssessmentList] = useState([]);
  const [teamList, setTeamList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  // Team selection state
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');

  // Enrollment manager state
  const [showEnrollmentManager, setShowEnrollmentManager] = useState(false);

  // Content manager state (evidence & actors)
  const [showContentManager, setShowContentManager] = useState(false);

  // Team manager state
  const [showTeamManager, setShowTeamManager] = useState(false);
  const [showGrupsExamen, setShowGrupsExamen] = useState(false);

  // Assessment action states
  const [actionLoading, setActionLoading] = useState(null); // assessmentId being actioned

  // Aquí hi ha l'enunciat i el briefing del cas, que és el text de més valor per
  // donar a una IA i és a un clic de l'exercici: bloquejar només la pantalla de
  // treball hi deixaria el forat obert. Mateixa mesura i mateixos límits que a
  // AssessmentWorkspacePage (vegeu utils/proteccioCopia.js).
  const avisCopia = useProteccioCopia(user?.role === 'analyst');

  const loadCase = useCallback(async () => {
    try {
      setLoading(true);
      const [caseRes, assessmentsRes] = await Promise.all([
        cases.getById(id),
        cases.getAssessments(id)
      ]);
      setCaseData(caseRes.data);
      setAssessmentList(assessmentsRes.data);

      // Load teams if team-based
      if (caseRes.data.isTeamBased) {
        const teamsRes = await cases.getTeams(id);
        setTeamList(teamsRes.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error carregant el cas');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCase();
  }, [loadCase]);

  const handleStartAssessment = async (teamId = null) => {
    // For team-based cases, show team selection modal first
    if (caseData?.isTeamBased && !teamId) {
      setShowTeamModal(true);
      return;
    }

    try {
      setStarting(true);
      const { data } = await cases.startAssessment(id, teamId ? { teamId } : {});
      navigate(`/assessments/${data.id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Error iniciant l\'avaluació');
      setStarting(false);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;

    try {
      setCreatingTeam(true);
      const { data } = await cases.createTeam(id, { name: newTeamName.trim() });
      setTeamList(prev => [...prev, data]);
      setSelectedTeamId(data.id);
      setNewTeamName('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error creant l\'equip');
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleStartWithTeam = async () => {
    if (!selectedTeamId) {
      setError('Selecciona o crea un equip');
      return;
    }
    setShowTeamModal(false);
    await handleStartAssessment(selectedTeamId);
  };

  // Assessment control handlers (instructor/admin only)
  const handlePauseAssessment = async (assessmentId) => {
    try {
      setActionLoading(assessmentId);
      await assessments.pause(assessmentId);
      await loadCase();
    } catch (err) {
      setError(err.response?.data?.message || 'Error pausant l\'avaluació');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResumeAssessment = async (assessmentId) => {
    try {
      setActionLoading(assessmentId);
      await assessments.resume(assessmentId);
      await loadCase();
    } catch (err) {
      setError(err.response?.data?.message || 'Error reprenent l\'avaluació');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetAssessment = async (assessmentId) => {
    if (!window.confirm('ATENCIÓ: Això esborrarà TOT el treball de l\'estudiant i reiniciarà l\'exercici. Vols continuar?')) {
      return;
    }
    try {
      setActionLoading(assessmentId);
      await assessments.reset(assessmentId);
      await loadCase();
    } catch (err) {
      setError(err.response?.data?.message || 'Error reiniciant l\'avaluació');
    } finally {
      setActionLoading(null);
    }
  };

  // Find user's team for this case
  const myTeam = teamList.find(t =>
    t.members?.some(m => m.userId === user?.id)
  );

  // Find user's existing assessment (individual or team-based)
  const myAssessment = assessmentList.find(a =>
    a.user?.id === user?.id ||
    (a.team?.id && myTeam?.id && a.team.id === myTeam.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error && !caseData) {
    return (
      <div className="text-center py-12">
        <p className="text-error">{error}</p>
        <Link to="/projects" className="text-accent mt-4 inline-block">
          Tornar als projectes
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AvisCopia visible={avisCopia} />

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <Link
              to={`/projects/${caseData?.projectId}`}
              className="text-content-muted hover:text-content-secondary"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 className="text-2xl font-bold text-content">{caseData?.title}</h1>
          </div>
          <p className="mt-1 text-sm text-content-muted">
            Projecte: {caseData?.project?.title}
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            caseData?.difficulty === 'beginner' ? 'bg-success-subtle text-success-text' :
            caseData?.difficulty === 'intermediate' ? 'bg-warning-subtle text-warning-text' :
            'bg-error-subtle text-error-text'
          }`}>
            {DIFFICULTY_LABELS[caseData?.difficulty] || caseData?.difficulty}
          </span>
          {caseData?.isTeamBased && (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-accent-subtle text-accent">
              En equip
            </span>
          )}
          {caseData?.timeLimitMinutes && (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-info-subtle text-info-text flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {Math.floor(caseData.timeLimitMinutes / 60)}h {caseData.timeLimitMinutes % 60}min
            </span>
          )}
          {/* Manage Content and Inscriptions buttons for instructors */}
          {(user?.role === 'instructor' || user?.role === 'admin') && (
            <>
              <button
                onClick={() => setShowContentManager(true)}
                className="btn btn-secondary text-sm flex items-center"
              >
                <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Contingut
              </button>
              {caseData?.isTeamBased && (
                <button
                  onClick={() => setShowTeamManager(true)}
                  className="btn btn-secondary text-sm flex items-center"
                >
                  <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Equips
                </button>
              )}
              {!caseData?.isTeamBased && (
                <button
                  onClick={() => setShowGrupsExamen(true)}
                  className="btn btn-secondary text-sm flex items-center"
                >
                  <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  Codis d'examen
                </button>
              )}
              <button
                onClick={() => setShowEnrollmentManager(true)}
                className="btn btn-secondary text-sm flex items-center"
              >
                <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                Inscripcions
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-error-subtle border border-error/30 text-error-text px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Description */}
      {caseData?.description && (
        <div className="card">
          <h2 className="text-lg font-semibold text-content mb-2">Descripció</h2>
          <p className="text-content-secondary">{caseData.description}</p>
        </div>
      )}

      {/* Instructions */}
      {caseData?.instructions && (
        <div className="card">
          <h2 className="text-lg font-semibold text-content mb-2">Instruccions</h2>
          <MarkdownViewer content={caseData.instructions} />
        </div>
      )}

      {/* Action Panel */}
      <div className="card bg-accent-subtle border border-accent/30">
        <div className="flex items-center justify-between">
          <div>
            {myAssessment ? (
              <>
                <h3 className="font-semibold text-content">Continua el teu treball</h3>
                <p className="text-sm text-content-secondary">
                  Estat: <span className="font-medium">{STATUS_LABELS[myAssessment.status] || myAssessment.status}</span>
                </p>
              </>
            ) : (
              <>
                <h3 className="font-semibold text-content">Preparat per començar?</h3>
                <p className="text-sm text-content-secondary">
                  Comença la teva avaluació per aquest cas
                </p>
              </>
            )}
          </div>
          {myAssessment ? (
            <Link to={`/assessments/${myAssessment.id}`} className="btn btn-primary">
              Obrir espai de treball
            </Link>
          ) : (
            <button
              onClick={() => handleStartAssessment()}
              disabled={starting}
              className="btn btn-primary disabled:opacity-50"
            >
              {starting ? 'Iniciant...' : (caseData?.isTeamBased ? 'Seleccionar equip i començar' : 'Començar avaluació')}
            </button>
          )}
        </div>
      </div>

      {/* All Assessments (for instructors) */}
      {(user?.role === 'instructor' || user?.role === 'admin') && assessmentList.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold text-content mb-4">
            Totes les avaluacions ({assessmentList.length})
          </h2>
          <div className="space-y-3">
            {assessmentList.map((assessment) => (
              <div
                key={assessment.id}
                className="p-4 bg-surface-subtle rounded-lg"
              >
                <div className="flex items-center justify-between">
                  <Link
                    to={`/assessments/${assessment.id}`}
                    className="flex-1 hover:text-accent"
                  >
                    <p className="font-medium text-content">
                      {nomExecutor(assessment)}
                    </p>
                    <p className="text-sm text-content-muted">
                      Iniciat: {new Date(assessment.startedAt).toLocaleDateString('ca-ES')}
                    </p>
                  </Link>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      assessment.status === 'in_progress' ? 'bg-accent-subtle text-accent' :
                      assessment.status === 'submitted' ? 'bg-warning-subtle text-warning-text' :
                      assessment.status === 'graded' ? 'bg-success-subtle text-success-text' :
                      assessment.status === 'paused' ? 'bg-warning-subtle text-warning-text' :
                      'bg-surface-muted text-content'
                    }`}>
                      {STATUS_LABELS[assessment.status] || assessment.status}
                    </span>

                    {/* Action buttons */}
                    <div className="flex items-center space-x-1 ml-2">
                      {/* Pause button - only for in_progress */}
                      {assessment.status === 'in_progress' && (
                        <button
                          onClick={() => handlePauseAssessment(assessment.id)}
                          disabled={actionLoading === assessment.id}
                          className="p-1.5 text-orange-600 hover:text-warning-text hover:bg-warning-subtle rounded transition-colors disabled:opacity-50"
                          title="Pausar"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                      )}
                      {/* Resume button - only for paused */}
                      {assessment.status === 'paused' && (
                        <button
                          onClick={() => handleResumeAssessment(assessment.id)}
                          disabled={actionLoading === assessment.id}
                          className="p-1.5 text-green-600 hover:text-success-text hover:bg-success-subtle rounded transition-colors disabled:opacity-50"
                          title="Reprendre"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                      )}
                      {/* Reset button - only for finished assessments */}
                      {['submitted', 'time_expired', 'abandoned'].includes(assessment.status) && (
                        <button
                          onClick={() => handleResetAssessment(assessment.id)}
                          disabled={actionLoading === assessment.id}
                          className="p-1.5 text-red-600 hover:text-error-text hover:bg-error-subtle rounded transition-colors disabled:opacity-50"
                          title="Reiniciar (esborra tot el treball)"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Team Selection Modal */}
      {showTeamModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-content mb-4">Selecciona o crea un equip</h2>
              <p className="text-sm text-content-secondary mb-4">
                Aquest és un cas en equip. Selecciona un equip existent o crea'n un de nou.
              </p>

              {/* Existing teams */}
              {teamList.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-content-secondary mb-2">
                    Selecciona equip
                  </label>
                  <div className="space-y-2">
                    {teamList.map((team) => (
                      <label
                        key={team.id}
                        className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedTeamId === team.id
                            ? 'border-primary-500 bg-accent-subtle'
                            : 'border-border hover:bg-surface-subtle'
                        }`}
                      >
                        <input
                          type="radio"
                          name="team"
                          value={team.id}
                          checked={selectedTeamId === team.id}
                          onChange={(e) => setSelectedTeamId(e.target.value)}
                          className="h-4 w-4 text-primary-600"
                        />
                        <span className="ml-3">
                          <span className="font-medium text-content">{team.name}</span>
                          <span className="text-sm text-content-muted ml-2">
                            ({team.members?.length || 0} membres)
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Create new team */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-content-secondary mb-2">
                  O crea un nou equip
                </label>
                <form onSubmit={handleCreateTeam} className="flex space-x-2">
                  <input
                    type="text"
                    value={newTeamName}
                    onChange={(e) => setNewTeamName(e.target.value)}
                    placeholder="Nom de l'equip..."
                    className="input flex-1"
                  />
                  <button
                    type="submit"
                    disabled={creatingTeam || !newTeamName.trim()}
                    className="btn btn-secondary disabled:opacity-50"
                  >
                    {creatingTeam ? '...' : 'Crear'}
                  </button>
                </form>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowTeamModal(false);
                    setSelectedTeamId('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel·lar
                </button>
                <button
                  onClick={handleStartWithTeam}
                  disabled={!selectedTeamId || starting}
                  className="btn btn-primary disabled:opacity-50"
                >
                  {starting ? 'Iniciant...' : 'Començar amb l\'equip'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enrollment Manager Modal */}
      {showEnrollmentManager && (
        <EnrollmentManager
          caseId={id}
          isTeamBased={caseData?.isTeamBased}
          onClose={() => setShowEnrollmentManager(false)}
        />
      )}

      {showGrupsExamen && (
        <GrupsExamen
          caseId={id}
          caseTitle={caseData?.title}
          onClose={() => setShowGrupsExamen(false)}
        />
      )}

      {/* Content Manager Modal (Evidence, Actors & Briefing) */}
      {showContentManager && (
        <CaseContentManager
          caseId={id}
          caseData={caseData}
          onClose={() => setShowContentManager(false)}
          onCaseUpdate={loadCase}
        />
      )}

      {/* Team Manager Modal */}
      {showTeamManager && (
        <TeamManager
          caseId={id}
          onClose={() => {
            setShowTeamManager(false);
            loadCase(); // Reload to get updated team data
          }}
        />
      )}
    </div>
  );
}

export default CaseDetailPage;
